import { useState, useCallback } from 'react';
import { apiKeysApi } from '../api/apiKeys';
import type { ApiKey, ApiScope, ApiKeyCreated } from '../api/apiKeys';

export function useApiKeys() {
  const [keys, setKeys]     = useState<ApiKey[]>([]);
  const [scopes, setScopes] = useState<ApiScope[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState<number | null>(null);
  const [error, setError]   = useState<string | null>(null);
  const [newKey, setNewKey] = useState<ApiKeyCreated | null>(null);

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [keysRes, scopesRes] = await Promise.all([
        apiKeysApi.list(),
        apiKeysApi.scopes(),
      ]);
      setKeys(keysRes.data.data);
      setScopes(scopesRes.data.data);
    } catch {
      setError('Failed to load API keys.');
    } finally {
      setLoading(false);
    }
  }, []);

  const create = useCallback(async (name: string, selectedScopes: string[]) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiKeysApi.create(name, selectedScopes);
      setNewKey(res.data.data);
      setKeys((prev) => [res.data.data, ...prev]);
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to create API key.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const rotate = useCallback(async (id: number) => {
    setActionId(id);
    setError(null);
    try {
      const res = await apiKeysApi.rotate(id);
      setNewKey(res.data.data);
      setKeys((prev) => prev.map((k) => (k.id === id ? res.data.data : k)));
      return res.data.data;
    } catch (err: any) {
      setError(err?.message ?? 'Failed to rotate key.');
      return null;
    } finally {
      setActionId(null);
    }
  }, []);

  const revoke = useCallback(async (id: number) => {
    setActionId(id);
    setError(null);
    try {
      const res = await apiKeysApi.revoke(id);
      setKeys((prev) => prev.map((k) => (k.id === id ? res.data.data : k)));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to revoke key.');
    } finally {
      setActionId(null);
    }
  }, []);

  const reactivate = useCallback(async (id: number) => {
    setActionId(id);
    setError(null);
    try {
      const res = await apiKeysApi.reactivate(id);
      setKeys((prev) => prev.map((k) => (k.id === id ? res.data.data : k)));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to reactivate key.');
    } finally {
      setActionId(null);
    }
  }, []);

  const remove = useCallback(async (id: number) => {
    setActionId(id);
    setError(null);
    try {
      await apiKeysApi.delete(id);
      setKeys((prev) => prev.filter((k) => k.id !== id));
    } catch (err: any) {
      setError(err?.message ?? 'Failed to delete key.');
    } finally {
      setActionId(null);
    }
  }, []);

  const dismissNewKey = useCallback(() => setNewKey(null), []);

  return {
    keys, scopes, loading, actionId, error, newKey,
    fetchKeys, create, rotate, revoke, reactivate, remove, dismissNewKey, setError,
  };
}
